import AWS from "aws-sdk";
import { v4 } from "uuid";

import {
  //   ArpabetToIpaTable,
  DRILL_PRONUNCIATION_S3_BUCKET_NAME,
  DRILL_PRONUNCIATION_S3_BUCKET_REGION,
  DRILL_PRONUNCIATION_S3_BUCKET_FOLDER,
  THRESHOLD_PHONOME_VALID_SCORE,
  DRILL_PRONUNCIATION_S3_UPLOAD_ID_POOL
} from "@tt-app/constants";
import { DrillPronunciationPhonemeEvaluation } from "@tt-app/models/exercise";
import { api } from "@tt-app/api";

/**
 * Upload the pronunciation drill audio to S3. A dedicated utility is implemented because:
 * - ACL "public-read" was need
 * - Pronunciation drills bucket is not served by `joyzbackend.com`
 * - Returned value as a Promise
 *
 * @param audio to be uploaded audio
 * @todo harmonize with the uploadToS3 utility in @joyz/app-core
 * @returns uploaded Audio file URL
 */
export const uploadPronunciationDrillAudioToS3 = async (audio: Blob): Promise<string> => {
  const filePath = `${DRILL_PRONUNCIATION_S3_BUCKET_FOLDER}/${v4()}.wav`;

  const credentials = new AWS.CognitoIdentityCredentials({
    IdentityPoolId: DRILL_PRONUNCIATION_S3_UPLOAD_ID_POOL
  });

  AWS.config.update({
    credentials,
    region: DRILL_PRONUNCIATION_S3_BUCKET_REGION
  });
  const S3 = new AWS.S3({ params: { Bucket: DRILL_PRONUNCIATION_S3_BUCKET_NAME } });

  const params: AWS.S3.Types.PutObjectRequest = {
    Bucket: DRILL_PRONUNCIATION_S3_BUCKET_NAME,
    Key: filePath,
    Body: audio,
    ACL: "public-read" // https://docs.aws.amazon.com/AmazonS3/latest/API/API_PutBucketAcl.html
  };

  const getAudioFileS3URL = (filePath: string): string =>
    `https://${DRILL_PRONUNCIATION_S3_BUCKET_NAME}.s3-${DRILL_PRONUNCIATION_S3_BUCKET_REGION}.amazonaws.com/${filePath}`;

  // Returns a Promise to leverage the `await`
  return new Promise((resolve, reject) => {
    // https://docs.aws.amazon.com/AWSJavaScriptSDK/latest/AWS/S3.html#putObject-property
    S3.putObject(params, (error, data) => {
      if (error) {
        reject(error);
        return;
      }

      if (data) {
        // console.log({ data });
        resolve(getAudioFileS3URL(filePath));
      }
    });
  });
};

/**
 * Submit an user audio for phoneme evalution to PETT.
 *
 * A same English word can have different pronunciation and consequently
 * different phonetics. Reason can be:
 * - English accent used (British, American, Canadian, Australian...)
 * - Homonym (lead 鉛 vs lead 率いる)
 * It is assumed that most cases will offer the same count of phonemes
 * with different content (vowels difference, mainly). In this case, it is
 * safe to do a one-to-one mapping between the IPA sent by the servers and
 * the phoneme sent by PETT
 *
 * @param audio User pronunciation drill audio
 * @param expectedWord User is expected to say this word
 * @param wordIPA Phonetics of the expectedWord sent by the server
 *
 * @returns An array of <IPA, isCorrect> pairs
 * @throws An error is the pettEvaluation results length is shorter than
 * the `wordIPA` length
 *
 * @see https://joyz.monday.com/boards/527178971/pulses/673793518
 */
export const evaluatePronunciation = async (
  audio: Blob,
  expectedWord: string,
  wordIPA: string[]
): Promise<DrillPronunciationPhonemeEvaluation[]> => {
  // Get the PETT analysis
  const pettEvaluation = await api.pett.sendForEvaluation(audio, expectedWord);

  if (!pettEvaluation.results) {
    throw new Error("PETT returned empty results");
  }

  // Ensure that PETT results has enough phonemes analysis. Similarly to iOS,
  // there is no check on phonemes mapping or length equality
  if (pettEvaluation.results.phones.length < wordIPA.length) {
    throw new Error("PETT results does not have enough phoneme");
  }

  const evaluation: DrillPronunciationPhonemeEvaluation[] = pettEvaluation.results.phones.map(
    (p, index): DrillPronunciationPhonemeEvaluation => ({
      arpabet: p[0],
      // ipa: ArpabetToIpaTable[p[0]], // -> Kept for reference
      ipa: wordIPA[index],
      isCorrect: p[1] > THRESHOLD_PHONOME_VALID_SCORE
    })
  );

  return evaluation;
};
